// src/components/ideaNiv/IdeaHistory/IdeaHistoryView.tsx
import { Box, Stack, Flex, Text, Timeline, Avatar, Group, Tooltip, createStyles } from '@mantine/core';
import { IconX } from '@tabler/icons';
import { MentionProvider } from '../MentionTextarea/MentionContext';
import { ProductAreaHeaderIcon, StatusHeaderIcon } from '../../icons/x-symbol-svgrepo-com';
import { OwnerHeaderIcon } from '../../icons/x-symbol-svgrepo-com';
import { formatDateTime } from '../../utils/dateFormats';
import { formatDate } from '../../utils';
import { ALLOWED_EVENTS_IN_HISTORY } from '../../types/constants';

interface IdeaHistoryProps {
  activityLogs: any[];
  idea: any;
  setIdeaHistoryView: (value: boolean) => void;
}


const getFieldIcon = (type: string) => {
  const defaultProps = {
    size: 14,
    stroke: 1.5,
    color: '#5C5CEB'
  };

  switch (type.toLowerCase()) {
  case 'ownername':
    return <OwnerHeaderIcon {...defaultProps} />;

  case 'status':
    return <StatusHeaderIcon {...defaultProps} />;

  case 'componentid':
    return <ProductAreaHeaderIcon {...defaultProps} />;

  }
};
// Add this constant for allowed fields
const ALLOWED_FIELDS = ['ownerName', 'componentId', 'status'];

const getReadableFieldName = (field: string) => {
  const fieldMap: { [key: string]: string } = {
    ownerName: 'Owner',
    componentId: 'Product Area',
    status: 'Status',
  };
  return fieldMap[field] || field;
};

const HistoryHeader = ({ onClose }: { onClose: () => void }) => (
  <Flex
    mt="24px"
    pb="16px" 
    px="35px"
    justify="space-between"
    align="center"
    sx={{ borderBottom: "1px solid #D8D8DB" }}
  >
    <Flex justify="space-between" w="100%" h={52} align="center">
      <Stack fz="20px" fw={600} h="20px" ta="center" justify="center">
        Idea History
      </Stack>
      <Flex
        mb={2}
        mr="8px"
        sx={{ cursor: "pointer" }}
        align="center"
        onClick={onClose}
      >
        <IconX size={14} color="#5C5CEB" />
        <Text ml="4px" color="#5C5CEB" fz={12} py={6}>
          Close
        </Text>
      </Flex>
    </Flex>
  </Flex>
);

const UserAvatar = ({ initial }: { initial: string }) => (
  <Avatar
    radius="xl"
    size={16}
    styles={(theme) => ({ 
      root: {
        backgroundColor: '#585C68',
        border: 'none',
        color: 'white',
      },
      placeholder: {
        color: 'white',
        fontSize: '10px',
        backgroundColor: '#585C68',
        fontWeight: 500
      }
    })}
  >
    {initial}
  </Avatar>
);

const ChangeItem = ({ field, value }: { field: string; value: any }) => (
  <Flex>
    <Flex
      w="24px"
      h="24px"
      m={3}
      bg="#E9ECEF"
      sx={{ borderRadius: 2, padding: 5, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
    >
      {getFieldIcon(field)}  
    </Flex>
    <Flex
      w="100%"
      h="100%"
      m={3}
      bg="#E9ECEF"
      sx={{ borderRadius: 2, padding: 3, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
    >
      <Text 
        size="sm" 
        style={{
          fontFamily: 'Inter',
          fontSize: 12,
          marginBottom: 2,
          letterSpacing: 0,
          verticalAlign: 'middle',
        }}
      >
        <Text span weight={100}>
          {getReadableFieldName(field)}
        </Text>
        {' '}was changed
        {' '}from{' '}
        <Text span weight={700} color="dark">
          "{value.from || 'none'}"
        </Text>
        {' '}to{' '}
        <Text span weight={700} color="dark">
          "{value.to || 'none'}"
        </Text>
      </Text>
    </Flex>
  </Flex>
);

const TimelineItemHeader = ({ user, timestamp }: { user: string; timestamp: string }) => (
  <Group spacing="xs">
    <UserAvatar initial={user?.charAt(0)} />
    <Text size={12} fw={800} styles={{
      root: {
        color: '#212529'
      }
    }}>
      {user || 'Unknown user'}
    </Text>
    <Tooltip
      label={formatDate(timestamp)}
      withArrow
      width={295}
    >
      <Text 
        size={12}
        c="dimmed"
        styles={{
          root: {
            color: '#868E96'
          }
        }}
      >
        • {formatDateTime(timestamp)}
      </Text>
    </Tooltip>
  </Group>
);

// Main component
export const IdeaHistoryView = ({ activityLogs, idea, setIdeaHistoryView }: IdeaHistoryProps) => {
  const useStyles = createStyles((theme) => ({
    historyContainer: {
      '& .mantine-d7wmdf:not(:last-of-type)::before': {
        display: 'none'
      }
    }
  }));
  const { classes } = useStyles();
    
  const timelineStyles = {
    root: {
      '&::before': {
        display: 'none'
      }
    },
    item: {
      '&::before': {
        display: 'none'
      },
      borderLeft: 'none !important',
      paddingLeft: 0
    },
    itemBullet: {
      display: 'none'
    }
  };

  return (
    <Box 
      bg="#FDFEFF"
      h='auto' 
      styles={(theme) => ({
        root: {
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': {
              background: '#a8a8a8',
            },
          }
        }
      })}
    >
      <MentionProvider>
        <Stack justify="space-between">
          <Box>
            <HistoryHeader onClose={() => setIdeaHistoryView(false)} />
            
            <Box p="md" style={{ 
              border: '1px solid #eee', 
              paddingLeft: 36, 
              paddingRight: 36, 
              width: '496px', 
              maxHeight: '741px', 
              height: '600px',
              overflowY: 'scroll'
            }}>
              <Timeline styles={timelineStyles} className={classes.historyContainer}>
                {activityLogs.map((log) => {
                  const changes = log.changes?.changes || {};
                  const filteredChanges = Object.entries(changes)
                    .filter(([field]) => ALLOWED_FIELDS.includes(field))
                    .filter(([_, value]: [string, any]) => 
                      value.from !== value.to && 
                      (value.from !== undefined || value.to !== undefined)
                    );

                  if ((log.eventType === 'idea_updated' && filteredChanges.length === 0) || !ALLOWED_EVENTS_IN_HISTORY.includes(log.eventType)) {
                    return null;
                  }

                  return (
                    <Timeline.Item
                      key={log._id}
                      title={<TimelineItemHeader user={log.createdBy} timestamp={log.createdAt} />}
                    >
                      <Stack spacing={0} ml="-10px" p={4} w="26.5rem">
                        {filteredChanges.map(([field, value]) => (
                          <ChangeItem key={field} field={field} value={value} />
                        ))}
                      </Stack>
                    </Timeline.Item>
                  );
                })}

                <Timeline.Item
                  title={<TimelineItemHeader user={idea?.owner?.name || idea.ownerName} timestamp={idea?.createdAt} />}
                >
                  <Stack spacing={4} ml={20} p={4} w="26rem">
                    <Text size="sm" color="dimmed">
                      Created the idea
                    </Text>
                  </Stack>
                </Timeline.Item>
              </Timeline>
            </Box>
          </Box>
        </Stack>
      </MentionProvider>
    </Box>
  );
};